import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Calendar, AlertTriangle, FileText, ArrowRight } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import {
  usePlanTemplates,
  useDeletePlanTemplate,
  useApplyPlanTemplate,
  usePatients,
} from '../lib/queries';
import { ApiError, type PlanTemplateRow } from '../lib/api';
import styles from './Dashboard.module.css';

function formatDateIT(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Sprint 9.B closure — Lista template piano alimentare riutilizzabili.
 * Azioni per riga: applica a paziente (apre modal) + elimina (soft via archived_at).
 * Creazione template: dal wizard PianoEditor via bottone "Salva come template".
 */
export default function Templates() {
  const navigate = useNavigate();
  const { data: templates, isLoading, error: loadError } = usePlanTemplates();
  const { data: patients } = usePatients();
  const deleteMutation = useDeletePlanTemplate();
  const applyMutation = useApplyPlanTemplate();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [applySource, setApplySource] = useState<PlanTemplateRow | null>(null);
  const [applyPatientId, setApplyPatientId] = useState<string>('');
  const [applyTitle, setApplyTitle] = useState<string>('');
  const [applyStartDate, setApplyStartDate] = useState<string>(() =>
    new Date().toISOString().split('T')[0]
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const sorted = useMemo(() => {
    return [...(templates || [])].sort((a, b) =>
      (b.updated_at || '').localeCompare(a.updated_at || '')
    );
  }, [templates]);

  const handleDelete = (tplId: string) => {
    setActionError(null);
    void (async () => {
      try {
        await deleteMutation.mutateAsync(tplId);
        setConfirmDeleteId(null);
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setActionError(e.message || String(err));
      }
    })();
  };

  const openApply = (tpl: PlanTemplateRow) => {
    setActionError(null);
    setApplySource(tpl);
    setApplyTitle(tpl.name);
    setApplyPatientId('');
    setApplyStartDate(new Date().toISOString().split('T')[0]);
  };

  const handleApply = () => {
    setActionError(null);
    if (!applySource) return;
    if (!applyPatientId) {
      setActionError('Seleziona un paziente');
      return;
    }
    if (!applyStartDate) {
      setActionError('Imposta data inizio');
      return;
    }
    void (async () => {
      try {
        const res = await applyMutation.mutateAsync({
          id: applySource.id,
          body: {
            patient_id: applyPatientId,
            title: applyTitle.trim() || applySource.name,
            start_date: applyStartDate,
          },
        });
        setApplySource(null);
        // Vai direttamente all'edit del piano appena creato
        navigate(`/piani/${res.plan_id}/edit`);
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setActionError(e.message || String(err));
      }
    })();
  };

  return (
    <>
      <ProTopBar customCtaLabel="Nuovo piano" onCta={() => navigate('/piani/new')} />

      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 700,
            letterSpacing: 1.6, color: 'var(--mute)', textTransform: 'uppercase',
          }}
        >
          Studio Pro · Piani · Template
        </div>
        <h1
          style={{
            fontFamily: 'var(--display)', fontWeight: 800, fontSize: 32,
            letterSpacing: -1, margin: '6px 0 0', lineHeight: 1.05,
          }}
        >
          Template piano
        </h1>
        <div style={{ fontSize: 13, color: 'var(--mute)', marginTop: 6 }}>
          Ricette di piano riutilizzabili. Applicale a un paziente per generare un nuovo piano in 1 click.
        </div>
      </div>

      {actionError && (
        <div
          role="alert"
          style={{
            background: 'var(--red-soft, #fff0f0)', border: '1px solid var(--red)',
            borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--red)',
            marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center',
          }}
        >
          <AlertTriangle size={16} />
          {actionError}
        </div>
      )}

      {loadError && (
        <div role="alert" style={{ color: 'var(--red)', padding: 12 }}>
          Errore caricamento template: {loadError instanceof ApiError ? loadError.message : String(loadError)}
        </div>
      )}

      {isLoading && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--mute)' }}>Caricamento…</div>
      )}

      {!isLoading && sorted.length === 0 && (
        <div
          className={styles['empty-state']}
          style={{
            textAlign: 'center', padding: 48,
            background: 'var(--paper)', border: '1px dashed var(--line)', borderRadius: 12,
          }}
        >
          <FileText size={36} style={{ color: 'var(--mute)', marginBottom: 12 }} />
          <h3 style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
            Nessun template salvato
          </h3>
          <p style={{ color: 'var(--mute)', fontSize: 13, marginBottom: 16, maxWidth: 360, marginInline: 'auto' }}>
            Apri un piano dall'editor e clicca "Salva come template" nel pannello revisione per creare la tua prima ricetta riutilizzabile.
          </p>
          <button
            type="button"
            onClick={() => navigate('/piani/new')}
            style={{
              background: 'var(--green-deep)', color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 999, fontWeight: 700, fontSize: 13,
              display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer',
            }}
          >
            <Plus size={14} /> Nuovo piano
          </button>
        </div>
      )}

      {sorted.length > 0 && (
        <div style={{ display: 'grid', gap: 12 }}>
          {sorted.map((tpl) => (
            <div
              key={tpl.id}
              style={{
                background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 12,
                padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>{tpl.name}</div>
                  {tpl.description && (
                    <div style={{ color: 'var(--mute)', fontSize: 13, marginTop: 4 }}>{tpl.description}</div>
                  )}
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--mute)', marginTop: 8, fontFamily: 'var(--mono)' }}>
                    <span>{tpl.weeks} settiman{tpl.weeks === 1 ? 'a' : 'e'}</span>
                    <span>·</span>
                    <span>Aggiornato {formatDateIT(tpl.updated_at)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => openApply(tpl)}
                    title="Applica a paziente"
                    style={{
                      background: 'var(--green-deep)', color: '#fff', border: 'none',
                      padding: '8px 14px', borderRadius: 999, fontWeight: 700, fontSize: 12,
                      display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer',
                    }}
                  >
                    Applica <ArrowRight size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(tpl.id)}
                    title="Elimina template"
                    style={{
                      background: 'transparent', border: '1px solid var(--line)',
                      padding: 8, borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={14} color="var(--red)" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal conferma delete */}
      {confirmDeleteId && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--paper)', borderRadius: 12, padding: 24,
              maxWidth: 400, width: '90%',
            }}
          >
            <h3 style={{ fontWeight: 800, marginBottom: 12 }}>Eliminare il template?</h3>
            <p style={{ color: 'var(--mute)', fontSize: 13, marginBottom: 20 }}>
              Il template verrà archiviato (soft-delete). I piani già applicati restano intatti.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: '1px solid var(--line)',
                  background: 'transparent', cursor: 'pointer',
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirmDeleteId)}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: 'none',
                  background: 'var(--red)', color: '#fff', cursor: 'pointer', fontWeight: 700,
                }}
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal applica template */}
      {applySource && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
          onClick={() => setApplySource(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--paper)', borderRadius: 12, padding: 24,
              maxWidth: 480, width: '90%',
            }}
          >
            <h3 style={{ fontWeight: 800, marginBottom: 6 }}>Applica template a paziente</h3>
            <div style={{ color: 'var(--mute)', fontSize: 12, marginBottom: 16 }}>
              Genera un nuovo piano per il paziente selezionato partendo da "{applySource.name}".
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              Paziente
            </label>
            <select
              value={applyPatientId}
              onChange={(e) => setApplyPatientId(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid var(--line)',
                borderRadius: 8, fontSize: 13, marginBottom: 12,
              }}
            >
              <option value="">— scegli paziente —</option>
              {(patients || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              Titolo piano
            </label>
            <input
              type="text"
              value={applyTitle}
              onChange={(e) => setApplyTitle(e.target.value)}
              placeholder={applySource.name}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid var(--line)',
                borderRadius: 8, fontSize: 13, marginBottom: 12, boxSizing: 'border-box',
              }}
            />

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              Data inizio
            </label>
            <input
              type="date"
              value={applyStartDate}
              onChange={(e) => setApplyStartDate(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid var(--line)',
                borderRadius: 8, fontSize: 13, marginBottom: 20, boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setApplySource(null)}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: '1px solid var(--line)',
                  background: 'transparent', cursor: 'pointer',
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applyMutation.isPending}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: 'none',
                  background: 'var(--green-deep)', color: '#fff', cursor: 'pointer', fontWeight: 700,
                  opacity: applyMutation.isPending ? 0.6 : 1,
                  display: 'inline-flex', gap: 6, alignItems: 'center',
                }}
              >
                <Calendar size={14} />
                {applyMutation.isPending ? 'Applicazione…' : 'Applica e modifica'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
